/**
 * Chat Flow Engine
 * Manages conversation flow state and navigation through JSON-defined flows
 */

import type { ChatFlow, FlowNode, FlowState, Message, QuickReplyOption } from '../types/flow';

export class ChatFlowEngine {
  private flow: ChatFlow;
  private state: FlowState;
  private messageIdCounter: number = 0;

  constructor(flow: ChatFlow) {
    this.flow = flow;
    this.state = {
      currentNode: flow.startNode,
      history: [],
      selections: {},
      startedAt: Date.now(),
      completed: false,
    };
  }

  /**
   * Get the current flow state
   */
  getState(): FlowState {
    return { ...this.state };
  }

  /**
   * Get the current node
   */
  getCurrentNode(): FlowNode | null {
    return this.flow.nodes[this.state.currentNode] || null;
  }

  /**
   * Get the initial message for starting the flow
   */
  getInitialMessage(): Message {
    const startNode = this.flow.nodes[this.flow.startNode];
    return this.createMessageFromNode(startNode);
  }

  /**
   * Process user selection and move to the next node
   * @param optionValue - The value of the selected option
   * @returns Array of messages (bot response), or empty array for CTA buttons
   */
  processUserSelection(optionValue: string): Message[] {
    const currentNode = this.getCurrentNode();
    if (!currentNode) {
      throw new Error('Current node not found');
    }

    // Find the selected option
    const selectedOption = currentNode.options?.find(
      (opt) => opt.value === optionValue
    );

    if (!selectedOption) {
      throw new Error('Invalid option selected');
    }

    // Store the user's selection
    this.state.selections[currentNode.id] = optionValue;

    // Create user message
    const userMessage: Message = {
      id: this.generateMessageId(),
      type: 'text',
      content: { text: selectedOption.label },
      position: 'right',
      timestamp: Date.now(),
      nodeId: currentNode.id,
    };

    // If this is a CTA button with URL, don't navigate
    if (selectedOption.url && selectedOption.isCTA) {
      // Return only user message, no navigation
      return [userMessage];
    }

    // Navigate to next node
    const nextNodeId = selectedOption.nextNode;
    if (!nextNodeId) {
      throw new Error('Next node is required for non-CTA options');
    }

    this.state.history.push(this.state.currentNode);
    this.state.currentNode = nextNodeId;

    const nextNode = this.flow.nodes[nextNodeId];
    if (!nextNode) {
      throw new Error(`Next node "${nextNodeId}" not found`);
    }

    // Check if flow is completed
    if (nextNode.type === 'end') {
      this.state.completed = true;
    }

    // Create bot message from next node
    const botMessage = this.createMessageFromNode(nextNode);

    return [userMessage, botMessage];
  }

  /**
   * Create a message object from a flow node
   */
  private createMessageFromNode(node: FlowNode): Message {
    const message: Message = {
      id: this.generateMessageId(),
      type: node.type === 'quickReply' ? 'quickReply' : 'text',
      content: {
        text: node.botMessage,
        options: node.options,
      },
      position: 'left',
      timestamp: Date.now(),
      nodeId: node.id,
    };

    return message;
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${this.messageIdCounter++}`;
  }

  /**
   * Check if the flow is completed
   */
  isCompleted(): boolean {
    return this.state.completed;
  }

  /**
   * Get user selections history
   */
  getSelections(): Record<string, string> {
    return { ...this.state.selections };
  }

  /**
   * Reset the flow to the beginning
   */
  reset(): void {
    this.state = {
      currentNode: this.flow.startNode,
      history: [],
      selections: {},
      startedAt: Date.now(),
      completed: false,
    };
    this.messageIdCounter = 0;
  }

  /**
   * Get flow metadata
   */
  getFlowInfo(): { id: string; name: string; description?: string } {
    return {
      id: this.flow.flowId,
      name: this.flow.name,
      description: this.flow.description,
    };
  }

  /**
   * Save state to localStorage
   */
  saveState(key: string = 'chatFlowState'): void {
    try {
      const stateData = {
        flowId: this.flow.flowId,
        state: this.state,
        messageIdCounter: this.messageIdCounter,
      };
      localStorage.setItem(key, JSON.stringify(stateData));
    } catch (error) {
      console.error('Failed to save flow state:', error);
    }
  }

  /**
   * Load state from localStorage
   */
  loadState(key: string = 'chatFlowState'): boolean {
    try {
      const saved = localStorage.getItem(key);
      if (!saved) return false;

      const stateData = JSON.parse(saved);

      // Only load if it's the same flow
      if (stateData.flowId !== this.flow.flowId) {
        return false;
      }

      this.state = stateData.state;
      this.messageIdCounter = stateData.messageIdCounter || 0;
      return true;
    } catch (error) {
      console.error('Failed to load flow state:', error);
      return false;
    }
  }

  /**
   * Clear saved state
   */
  clearSavedState(key: string = 'chatFlowState'): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear flow state:', error);
    }
  }

  /**
   * Validate a chat flow JSON structure
   */
  static validateFlow(flow: unknown): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!flow || typeof flow !== 'object') {
      return { valid: false, errors: ['Flow must be an object'] };
    }

    const flowObj = flow as Partial<ChatFlow> & {
      nodes?: Record<string, unknown>;
    };

    // Check required fields
    if (!flowObj.flowId) errors.push('Missing flowId');
    if (!flowObj.name) errors.push('Missing name');
    if (!flowObj.startNode) errors.push('Missing startNode');
    if (!flowObj.nodes || typeof flowObj.nodes !== 'object') {
      errors.push('Missing or invalid nodes');
      return { valid: false, errors };
    }

    const nodes = flowObj.nodes as Record<string, unknown>;

    // Check if startNode exists
    if (flowObj.startNode && !nodes[flowObj.startNode]) {
      errors.push(`Start node "${flowObj.startNode}" not found in nodes`);
    }

    // Validate each node
    Object.entries(nodes).forEach(([nodeId, nodeValue]) => {
      const node = nodeValue as Partial<FlowNode> & { options?: unknown };
      if (!node.id) errors.push(`Node "${nodeId}" missing id`);
      if (!node.type) errors.push(`Node "${nodeId}" missing type`);
      if (!node.botMessage) errors.push(`Node "${nodeId}" missing botMessage`);

      // Validate options for quickReply nodes
      if (node.type === 'quickReply') {
        if (!node.options || !Array.isArray(node.options)) {
          errors.push(`Node "${nodeId}" of type quickReply must have options array`);
        } else {
          node.options.forEach((optValue, idx: number) => {
            const opt = optValue as Partial<QuickReplyOption>;
            if (!opt.label) errors.push(`Node "${nodeId}" option ${idx} missing label`);
            if (!opt.value) errors.push(`Node "${nodeId}" option ${idx} missing value`);

            // Only validate nextNode if the option doesn't have a URL (CTA buttons)
            if (!opt.url && !opt.nextNode) {
              errors.push(`Node "${nodeId}" option ${idx} missing nextNode (required for non-URL options)`);
            }

            if (opt.nextNode && !nodes[opt.nextNode]) {
              errors.push(`Node "${nodeId}" option "${opt.label}" references non-existent node "${opt.nextNode}"`);
            }
          });
        }
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Load a flow from JSON file
   */
  static async loadFlowFromJSON(jsonPath: string): Promise<ChatFlow> {
    try {
      const response = await fetch(jsonPath);
      if (!response.ok) {
        throw new Error(`Failed to load flow: ${response.statusText}`);
      }
      const flow = await response.json();

      // Validate the flow
      const validation = ChatFlowEngine.validateFlow(flow);
      if (!validation.valid) {
        throw new Error(`Invalid flow: ${validation.errors.join(', ')}`);
      }

      return flow;
    } catch (error) {
      console.error('Error loading flow:', error);
      throw error;
    }
  }
}

export default ChatFlowEngine;
